<template>
	<div class="feedback-container">
		<div class="feedback-form">
			<FieldsPanel :fields="personalFields" @onUpdateAreaCode="handleAreaCodeUpdate" />
		</div>
		<div class="feedback-footer">
			<div class="feedback-footer-btn" @click="cancelFeedback">{{ $t('enterprise.cancel') }}</div>
			<div :class="['feedback-footer-btn', isDisabled && 'disabled']" @click="submitFeedback">
				{{ $t('enterprise.submit') }}
			</div>
		</div>
	</div>
</template>

<script setup>
import { ref, computed, onMounted, getCurrentInstance } from 'vue';
import { useRouter, useStore } from '@/utils/vue-api';
import { Toast } from 'vant';
import { debounce } from '@/utils/api';
import { useModuleData } from '@rebuild/hooks/data/useModuleData';
import { useNavigation } from '@rebuild/hooks/useNavigation';
import FieldsPanel from '../appointment/custom-fields/FieldsPanel.vue';
import { usePersonalFields } from '../appointment/custom-fields/usePersonalFields';

const { proxy } = getCurrentInstance();

const router = useRouter();
const store = useStore();

const { leave_word_create, leave_word_config } = useModuleData();
const { replaceModuleSubPath } = useNavigation();

const {
	personalFields,
	getSystemFields,
	getCustomFields,
	getSystemField,
	getSystemFieldValue,
	initDynamicField,
	validateFields,
	processCustomFields,
	saveCacheDynamicFieldConfig,
	getCacheDynamicFieldConfig
} = usePersonalFields();

const DEFAULT_NUMBER_CODE = '86';
const landAreaCode = ref(DEFAULT_NUMBER_CODE);
const handleAreaCodeUpdate = areaCode => {
	landAreaCode.value = areaCode;
};

const showTooltip = message => {
	store.dispatch('showTooltip', message);
};

const cancelFeedback = debounce(
	function () {
		router.back();
	},
	180,
	true
);

const validateInputFields = () => {
	const systemFields = getSystemFields.value;
	const customFields = getCustomFields.value;
	return validateFields([...systemFields, ...customFields]);
};
const isDisabled = computed(() => {
	return validateInputFields();
});

const validateInput = () => {
	const emailField = getSystemField(5);
	if (emailField && emailField.dynamicFieldValue) {
		const emailRegex = /^\w+([-+.]\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*$/;
		if (!emailRegex.test(emailField.dynamicFieldValue)) {
			return proxy.$t('enterprise.field.success-email');
		}
	}
	// 其他空值情况，已经被 isDisabled 拦截掉

	return null;
};

const submitFeedback = debounce(
	async function () {
		if (isDisabled.value) return;

		const validateErr = validateInput();
		if (validateErr) {
			return showTooltip(validateErr);
		}

		Toast.loading({
			message: 'Loading...',
			forbidClick: true,
			duration: 0
		});

		const question = getSystemFieldValue(7);
		const name = getSystemFieldValue(1);
		const phone = getSystemFieldValue(2);
		const email = getSystemFieldValue(5);

		const params = {
			name,
			email,
			phone,
			question,
			areaCode: landAreaCode.value ? `+${landAreaCode.value}` : `+${DEFAULT_NUMBER_CODE}`
		};

		const processedCustomFields = processCustomFields();
		if (processedCustomFields.length > 0) {
			params.newAddDataFiled = processedCustomFields;
		}
		const cacheDynamicFieldConfigVO = getCacheDynamicFieldConfig();
		if (cacheDynamicFieldConfigVO) {
			params.dynamicFieldConfigVO = cacheDynamicFieldConfigVO;
		}

		await leave_word_create.refresh(params);

		if (leave_word_create.error) {
			Toast.clear();
			Toast(proxy.$t('enterprise.error'));
			return;
		}

		Toast.clear();
		replaceModuleSubPath('leave_word', 'result');
	},
	180,
	true
);

const initLeaveWordConfig = async () => {
	await leave_word_config.refresh();

	const { dynamicFieldConfigVO, languageDynamicFieldConfigVO } = leave_word_config.data?.data || {};

	dynamicFieldConfigVO && saveCacheDynamicFieldConfig(dynamicFieldConfigVO);
	initDynamicField(languageDynamicFieldConfigVO);
};
onMounted(() => {
	initLeaveWordConfig();
});
</script>

<style lang="less" scoped>
@import '@/style/mixins.less';

.feedback-container {
	height: 100%;
	overflow: hidden;
	display: flex;
	flex-direction: column;

	.feedback-form {
		flex: 1;
		margin-top: 0.14rem;
		padding: 0 0.14rem;
		overflow-y: auto;
	}
	.feedback-footer {
		background: #fff;
		padding: 0.12rem 0.14rem;
		padding-bottom: calc(0.12rem + constant(safe-area-inset-bottom));
		padding-bottom: calc(0.12rem + env(safe-area-inset-bottom));
		box-shadow: 0px -0.03rem 0.07rem rgba(0, 0, 0, 0.02);
		display: flex;
		justify-content: space-between;

		.feedback-footer-btn {
			width: calc(50% - 0.06rem);
			text-align: center;
			line-height: 0.42rem;
			font-family: 'PingFang SC';
			font-size: 0.16rem;
			font-style: normal;
			font-weight: 400;
			border-radius: 0.01rem;

			&:nth-child(1) {
				background: #f6f7f8;
				color: #666;
			}
			&:nth-child(2) {
				background: var(--ir-theme-color);
				color: #fff;
			}
		}
		.disabled {
			opacity: 0.5;
		}
	}
}
</style>
